import { useState, useEffect, useRef, useCallback } from 'react';
import { MapContainer, TileLayer, Marker, Popup, useMap } from 'react-leaflet';
import L from 'leaflet';
import localforage from 'localforage';
import { 
  Download, 
  Wifi, 
  WifiOff, 
  Map as MapIcon, 
  Trash2, 
  Check, 
  AlertCircle,
  Loader2,
  MapPin,
  Navigation
} from 'lucide-react';

// Initialize offline storage
const tileStore = localforage.createInstance({
  name: 'ospitai-offline-maps',
  storeName: 'tiles'
});

const metaStore = localforage.createInstance({
  name: 'ospitai-offline-maps',
  storeName: 'metadata'
});

// Puglia regions to download
const REGIONS = {
  monopoli: {
    name: 'Monopoli',
    center: [40.9508, 17.2938],
    bounds: [[40.90, 17.20], [41.00, 17.40]],
    zoom: { min: 12, max: 16 }
  },
  polignano: {
    name: 'Polignano a Mare',
    center: [40.9967, 17.2210],
    bounds: [[40.95, 17.15], [41.05, 17.30]],
    zoom: { min: 12, max: 16 }
  },
  fasano: {
    name: 'Fasano',
    center: [40.8340, 17.3600],
    bounds: [[40.75, 17.25], [40.92, 17.50]],
    zoom: { min: 12, max: 16 }
  },
  rural: {
    name: 'Rural Puglia',
    center: [40.9000, 17.3000],
    bounds: [[40.70, 17.00], [41.10, 17.60]],
    zoom: { min: 10, max: 14 }
  }
};

// Points of interest for offline markers
const OFFLINE_POIS = [
  { id: 1, name: 'Lama Monachile Beach', lat: 40.9965, lng: 17.2198, type: 'beach' },
  { id: 2, name: 'Monopoli Old Port', lat: 40.9513, lng: 17.2961, type: 'landmark' },
  { id: 3, name: 'Zoosafari Fasano', lat: 40.8390, lng: 17.3520, type: 'attraction' },
  { id: 4, name: 'Egnazia Archaeological Park', lat: 40.8812, lng: 17.3889, type: 'culture' },
  { id: 5, name: 'Alberobello Trulli', lat: 40.7835, lng: 17.2365, type: 'landmark' },
  { id: 6, name: 'Porto Ghiacciolo', lat: 40.9432, lng: 17.2823, type: 'beach' },
  { id: 7, name: 'Selva di Fasano', lat: 40.8280, lng: 17.3280, type: 'nature' },
  { id: 8, name: 'Cala Paura', lat: 40.9930, lng: 17.2240, type: 'beach' },
  { id: 9, name: 'Masseria Torre Coccaro', lat: 40.8590, lng: 17.3710, type: 'landmark' },
  { id: 10, name: 'Grotte di Castellana', lat: 40.8756, lng: 17.1516, type: 'attraction' }
];

// Custom marker icons
const createIcon = (color) => L.divIcon({
  className: 'custom-marker',
  html: `<div style="background-color: ${color}; width: 24px; height: 24px; border-radius: 50%; border: 3px solid white; box-shadow: 0 2px 4px rgba(0,0,0,0.3);"></div>`,
  iconSize: [24, 24],
  iconAnchor: [12, 12]
});

const MARKER_COLORS = {
  beach: '#0891B2',
  landmark: '#7C3AED',
  attraction: '#EC4899',
  culture: '#D97706',
  nature: '#059669'
};

// Offline tile layer component
function OfflineTileLayer({ isOffline }) {
  const map = useMap();
  
  useEffect(() => {
    if (isOffline) {
      // Use cached tiles
      const offlineLayer = L.tileLayer('', {
        tileSize: 256,
        attribution: 'Offline Mode'
      });
      
      offlineLayer.createTile = function(coords, done) {
        const tile = document.createElement('img');
        const key = `${coords.z}/${coords.x}/${coords.y}`;
        
        tileStore.getItem(key).then(blob => {
          if (blob) {
            tile.src = URL.createObjectURL(blob);
          } else {
            tile.src = 'data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAAAEAAAABCAQAAAC1HAwCAAAAC0lEQVR42mNkYAAAAAYAAjCB0C8AAAAASUVORK5CYII=';
          }
          done(null, tile);
        }).catch(() => {
          done(null, tile);
        });
        
        return tile;
      };
      
      map.eachLayer(layer => {
        if (layer instanceof L.TileLayer) {
          map.removeLayer(layer);
        }
      });
      offlineLayer.addTo(map);
    }
  }, [isOffline, map]);
  
  if (isOffline) return null;
  
  return (
    <TileLayer
      url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
      attribution='&copy; OpenStreetMap'
    />
  );
}

// Download progress component
function DownloadProgress({ progress, total, region }) {
  const percentage = total > 0 ? Math.round((progress / total) * 100) : 0;
  
  return (
    <div className="bg-teal-50 rounded-lg p-4 mb-4">
      <div className="flex items-center justify-between mb-2">
        <span className="text-sm font-medium text-teal-700">
          Downloading {region}...
        </span>
        <span className="text-sm text-teal-600">{percentage}%</span>
      </div>
      <div className="w-full bg-teal-200 rounded-full h-2">
        <div 
          className="bg-teal-600 h-2 rounded-full transition-all duration-300"
          style={{ width: `${percentage}%` }}
        />
      </div>
      <p className="text-xs text-teal-600 mt-2">
        {progress} / {total} tiles
      </p>
    </div>
  );
}

export default function OfflineMapsPage() {
  const [isOnline, setIsOnline] = useState(navigator.onLine);
  const [downloadedRegions, setDownloadedRegions] = useState({});
  const [isDownloading, setIsDownloading] = useState(false);
  const [downloadProgress, setDownloadProgress] = useState({ progress: 0, total: 0, region: '' });
  const [selectedRegion, setSelectedRegion] = useState('monopoli');
  const [storageUsed, setStorageUsed] = useState(0);
  const [showMap, setShowMap] = useState(false);
  const mapRef = useRef(null);

  // Check online status
  useEffect(() => {
    const handleOnline = () => setIsOnline(true);
    const handleOffline = () => setIsOnline(false);
    
    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);
    
    return () => {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
    };
  }, []);

  // Load downloaded regions from storage
  useEffect(() => {
    const loadMetadata = async () => {
      try {
        const regions = await metaStore.getItem('downloadedRegions') || {};
        setDownloadedRegions(regions);
        
        // Calculate storage used
        let totalSize = 0;
        await tileStore.iterate((value) => {
          if (value instanceof Blob) {
            totalSize += value.size;
          }
        });
        setStorageUsed(totalSize);
      } catch (e) {
        console.error('Error loading metadata:', e);
      }
    };
    loadMetadata();
  }, []);

  // Generate tile coordinates for a region
  const getTileCoords = useCallback((bounds, minZoom, maxZoom) => {
    const coords = [];
    
    for (let z = minZoom; z <= maxZoom; z++) {
      const [[minLat, minLng], [maxLat, maxLng]] = bounds;
      
      const minTileX = Math.floor((minLng + 180) / 360 * Math.pow(2, z));
      const maxTileX = Math.floor((maxLng + 180) / 360 * Math.pow(2, z));
      const minTileY = Math.floor((1 - Math.log(Math.tan(maxLat * Math.PI / 180) + 1 / Math.cos(maxLat * Math.PI / 180)) / Math.PI) / 2 * Math.pow(2, z));
      const maxTileY = Math.floor((1 - Math.log(Math.tan(minLat * Math.PI / 180) + 1 / Math.cos(minLat * Math.PI / 180)) / Math.PI) / 2 * Math.pow(2, z));
      
      for (let x = minTileX; x <= maxTileX; x++) {
        for (let y = minTileY; y <= maxTileY; y++) {
          coords.push({ z, x, y });
        }
      }
    }
    
    return coords;
  }, []);

  // Download tiles for a region
  const downloadRegion = async (regionKey) => {
    if (isDownloading) return;
    
    const region = REGIONS[regionKey];
    if (!region) return;
    
    setIsDownloading(true);
    setDownloadProgress({ progress: 0, total: 0, region: region.name });
    
    try {
      const coords = getTileCoords(region.bounds, region.zoom.min, region.zoom.max);
      setDownloadProgress(prev => ({ ...prev, total: coords.length }));
      
      let downloaded = 0;
      const batchSize = 10;
      
      for (let i = 0; i < coords.length; i += batchSize) {
        const batch = coords.slice(i, i + batchSize);
        
        await Promise.all(batch.map(async ({ z, x, y }) => {
          const key = `${z}/${x}/${y}`;
          
          // Check if already cached
          const existing = await tileStore.getItem(key);
          if (existing) {
            downloaded++;
            return;
          }
          
          try {
            const url = `https://tile.openstreetmap.org/${z}/${x}/${y}.png`;
            const response = await fetch(url);
            
            if (response.ok) {
              const blob = await response.blob();
              await tileStore.setItem(key, blob);
            }
          } catch (e) {
            // Silently fail for individual tiles
          }
          downloaded++;
        }));
        
        setDownloadProgress(prev => ({ ...prev, progress: downloaded }));
        
        // Small delay to avoid rate limiting
        await new Promise(resolve => setTimeout(resolve, 100));
      }
      
      // Save metadata
      const updatedRegions = {
        ...downloadedRegions,
        [regionKey]: {
          name: region.name,
          downloadedAt: new Date().toISOString(),
          tileCount: coords.length
        }
      };
      await metaStore.setItem('downloadedRegions', updatedRegions);
      setDownloadedRegions(updatedRegions);
      
      // Update storage calculation
      let totalSize = 0;
      await tileStore.iterate((value) => {
        if (value instanceof Blob) totalSize += value.size;
      });
      setStorageUsed(totalSize);
      
    } catch (e) {
      console.error('Download error:', e);
    } finally {
      setIsDownloading(false);
      setDownloadProgress({ progress: 0, total: 0, region: '' });
    }
  };

  // Delete a region's tiles
  const deleteRegion = async (regionKey) => {
    if (isDownloading) return;
    
    const region = REGIONS[regionKey];
    if (!region) return;
    
    try {
      const coords = getTileCoords(region.bounds, region.zoom.min, region.zoom.max);
      
      for (const { z, x, y } of coords) {
        const key = `${z}/${x}/${y}`;
        await tileStore.removeItem(key);
      }
      
      const updatedRegions = { ...downloadedRegions };
      delete updatedRegions[regionKey];
      await metaStore.setItem('downloadedRegions', updatedRegions);
      setDownloadedRegions(updatedRegions);
      
      // Update storage
      let totalSize = 0;
      await tileStore.iterate((value) => {
        if (value instanceof Blob) totalSize += value.size;
      });
      setStorageUsed(totalSize);
      
    } catch (e) {
      console.error('Delete error:', e);
    }
  };

  const formatBytes = (bytes) => {
    if (bytes === 0) return '0 B';
    const k = 1024;
    const sizes = ['B', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(1)) + ' ' + sizes[i];
  };

  const region = REGIONS[selectedRegion];

  return (
    <div className="min-h-screen bg-stone-50 pb-24" data-testid="offline-maps-page">
      {/* Header */}
      <div className="bg-gradient-to-b from-stone-900 to-stone-800 text-white px-6 pt-8 pb-6">
        <div className="max-w-4xl mx-auto">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-teal-400 text-xs tracking-[0.2em] font-semibold uppercase">OFFLINE MAPS</p>
              <h1 className="text-2xl md:text-3xl font-serif mt-2">
                Maps for Rural Puglia
              </h1>
              <p className="text-stone-400 text-sm mt-1">
                Download maps for offline navigation in areas without connectivity
              </p>
            </div>
            <div className={`flex items-center gap-2 px-3 py-1.5 rounded-full ${isOnline ? 'bg-green-500/20 text-green-300' : 'bg-amber-500/20 text-amber-300'}`}>
              {isOnline ? <Wifi size={16} /> : <WifiOff size={16} />}
              <span className="text-sm font-medium">{isOnline ? 'Online' : 'Offline'}</span>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-4xl mx-auto px-4 py-6">
        {/* Storage Info */}
        <div className="bg-white rounded-xl p-4 border border-stone-200 mb-6">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-teal-100 rounded-lg">
                <MapIcon size={20} className="text-teal-600" />
              </div>
              <div>
                <p className="font-medium text-stone-900">Offline Storage</p>
                <p className="text-sm text-stone-500">{formatBytes(storageUsed)} used</p>
              </div>
            </div>
            <div className="text-right">
              <p className="text-sm text-stone-600">
                {Object.keys(downloadedRegions).length} regions saved
              </p>
            </div>
          </div>
        </div>

        {/* Download Progress */}
        {isDownloading && (
          <DownloadProgress 
            progress={downloadProgress.progress}
            total={downloadProgress.total}
            region={downloadProgress.region}
          />
        )}

        {/* Region Cards */}
        <div className="space-y-3 mb-6">
          {Object.entries(REGIONS).map(([key, reg]) => {
            const isDownloaded = !!downloadedRegions[key];
            
            return (
              <div 
                key={key}
                className={`bg-white rounded-xl p-4 border ${
                  selectedRegion === key ? 'border-teal-500 ring-1 ring-teal-500' : 'border-stone-200'
                }`}
                onClick={() => setSelectedRegion(key)}
              >
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <div className={`p-2 rounded-lg ${isDownloaded ? 'bg-green-100' : 'bg-stone-100'}`}>
                      <MapPin size={20} className={isDownloaded ? 'text-green-600' : 'text-stone-500'} />
                    </div>
                    <div>
                      <p className="font-medium text-stone-900">{reg.name}</p>
                      {isDownloaded && (
                        <p className="text-xs text-green-600 flex items-center gap-1">
                          <Check size={12} />
                          Downloaded {new Date(downloadedRegions[key].downloadedAt).toLocaleDateString()}
                        </p>
                      )}
                    </div>
                  </div>
                  
                  <div className="flex items-center gap-2">
                    {isDownloaded ? (
                      <button
                        onClick={(e) => { e.stopPropagation(); deleteRegion(key); }}
                        className="p-2 text-red-500 hover:bg-red-50 rounded-lg transition-colors"
                        disabled={isDownloading}
                      >
                        <Trash2 size={18} />
                      </button>
                    ) : (
                      <button
                        onClick={(e) => { e.stopPropagation(); downloadRegion(key); }}
                        disabled={isDownloading || !isOnline}
                        className={`flex items-center gap-2 px-3 py-1.5 rounded-lg text-sm font-medium transition-colors ${
                          isDownloading || !isOnline
                            ? 'bg-stone-100 text-stone-400 cursor-not-allowed'
                            : 'bg-teal-600 text-white hover:bg-teal-700'
                        }`}
                      >
                        {isDownloading ? (
                          <Loader2 size={16} className="animate-spin" />
                        ) : (
                          <Download size={16} />
                        )}
                        Download
                      </button>
                    )}
                  </div>
                </div>
              </div>
            );
          })}
        </div>

        {/* Offline Warning */}
        {!isOnline && Object.keys(downloadedRegions).length === 0 && (
          <div className="bg-amber-50 border border-amber-200 rounded-xl p-4 mb-6 flex items-start gap-3">
            <AlertCircle className="text-amber-500 flex-shrink-0 mt-0.5" size={20} />
            <div>
              <p className="font-medium text-amber-800">You're offline</p>
              <p className="text-sm text-amber-600">
                Connect to the internet to download maps for offline use.
              </p>
            </div>
          </div>
        )}

        {/* Toggle Map View */}
        <button
          onClick={() => setShowMap(!showMap)}
          className="w-full bg-teal-600 hover:bg-teal-700 text-white py-3 rounded-xl font-medium flex items-center justify-center gap-2 mb-6"
        >
          <Navigation size={18} />
          {showMap ? 'Hide Map' : 'View Map'}
        </button>

        {/* Map Display */}
        {showMap && region && (
          <div className="bg-white rounded-xl overflow-hidden border border-stone-200 mb-6">
            <div className="h-[400px]">
              <MapContainer
                center={region.center}
                zoom={13}
                style={{ height: '100%', width: '100%' }}
                ref={mapRef}
              >
                <OfflineTileLayer isOffline={!isOnline && !!downloadedRegions[selectedRegion]} />
                
                {/* POI Markers */}
                {OFFLINE_POIS.map(poi => (
                  <Marker
                    key={poi.id}
                    position={[poi.lat, poi.lng]}
                    icon={createIcon(MARKER_COLORS[poi.type] || '#666')}
                  >
                    <Popup>
                      <div className="text-center">
                        <p className="font-semibold">{poi.name}</p>
                        <p className="text-xs text-stone-500 capitalize">{poi.type}</p>
                      </div>
                    </Popup>
                  </Marker>
                ))}
              </MapContainer>
            </div>
            <div className="p-3 bg-stone-50 border-t border-stone-200">
              <p className="text-xs text-stone-500 text-center">
                {!isOnline && downloadedRegions[selectedRegion] 
                  ? 'Using offline tiles' 
                  : isOnline 
                    ? 'Live map - download for offline use' 
                    : 'No offline data for this region'}
              </p>
            </div>
          </div>
        )}

        {/* POI List */}
        <div className="bg-white rounded-xl border border-stone-200 overflow-hidden">
          <div className="p-4 border-b border-stone-100">
            <h3 className="font-semibold text-stone-900">Saved Places</h3>
            <p className="text-sm text-stone-500">Key locations available offline</p>
          </div>
          <div className="divide-y divide-stone-100">
            {OFFLINE_POIS.slice(0, 6).map(poi => (
              <div key={poi.id} className="p-4 flex items-center gap-3">
                <div 
                  className="w-3 h-3 rounded-full"
                  style={{ backgroundColor: MARKER_COLORS[poi.type] }}
                />
                <div>
                  <p className="font-medium text-stone-900 text-sm">{poi.name}</p>
                  <p className="text-xs text-stone-500 capitalize">{poi.type}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}
